import type {
  ITemplate,
  ITemplateCreate,
  ITemplateListQuery,
  ITemplateListResponse,
  ITemplateUpdate,
} from '@local/lib';

import { QualityAdjustmentType } from '@local/lib';

export type { TemplateScope } from '../../api/templatesApi';

// Labels for `QualityAdjustmentType` (see qualityFormulaMetadata.ts's `getFormulaAdjustmentCategory`
// for the formula-to-category mapping itself) — kept here since Template Manager is the only
// screen that currently displays this classification.
export const ADJUSTMENT_CATEGORY_LABEL: Record<QualityAdjustmentType, string> = {
  [QualityAdjustmentType.Price]: 'Price',
  [QualityAdjustmentType.Quantity]: 'Quantity',
};

/**
 * A reusable, business-named formula for exactly one Quality Specification (Moisture, HGI, Ash,
 * etc.) that a contract — or another template — can load as a starting point. Templates are
 * intentionally scoped to a single spec (not a bundle of specs) so "Moisture templates" and "HGI
 * templates" never mix, even when they happen to share the same underlying formula shape — see
 * `useTemplatesForSpec` for how that scoping is enforced without any per-spec branching, so adding
 * a new Quality Specification never requires touching template filtering logic.
 *
 * Loading a template only ever *copies* `config` into a contract's own state (see
 * `applyTemplateToSpec` in `specFormSlice.ts`) — editing it afterward never writes
 * back here.
 *
 * Alias of `ITemplate` (see `packages/@local/lib/src/dtos/TemplateDto.ts`) — the Pricing Engine
 * API's `/templates` resource response/record shape (see `templatesApi.ts` and
 * `useCustomTemplates.ts`), kept under this domain-specific name since every existing page/
 * component already imports it that way. `config` is `ITemplateConfig` — the same QualityDto
 * formula-spec shape the quality-adjustments calculate endpoint validates against — rather than
 * this page's own `TemplateSpecConfig` form-state shape; see `templateConfigConversion.ts` for the
 * boundary that converts between the two.
 */
export type QualityAdjustmentTemplate = ITemplate;

// Generic "custom template" naming used by the API/React Query data layer (templatesApi.ts,
// useCustomTemplates.ts) — kept as an alias of the domain-specific `QualityAdjustmentTemplate`
// name every existing page/component already imports, rather than two divergent shapes.
export type ICustomTemplate = QualityAdjustmentTemplate;

export type ICustomTemplateCreate = ITemplateCreate;

export type ICustomTemplateUpdate = ITemplateUpdate;

export type ICustomTemplateListQuery = ITemplateListQuery;

export type ICustomTemplateListResponse = ITemplateListResponse;

// A template only captures what makes a formula reusable — formula type, precision, tier mode,
// contracted value, and rows — never `enabled`/`collapsed`, which are per-card UI state that only
// make sense while a specific contract's (or template's own editor's) form is open. Defined in
// `qualityFormulaTypes.ts` (re-exported here for every existing call site) since it's owned in
// terms of `SpecState`, which both this page and the playground's own cards consume. This is the
// Quality Formulas playground's own form-state shape; `toTemplateConfig`/`fromTemplateConfig`
// (see `templateConfigConversion.ts`) convert it to/from `ITemplateConfig`, the shape actually
// persisted through the API (see `TemplateDto.ts`).
export type { TemplateSpecConfig } from '../quality-formulas/qualityFormulaTypes';
